import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { WizbiiCarouselActions } from '@shared/wizbii-carousel/carousel-actions.enum';
import { WzbCarouselSlideComponent } from '@shared/wizbii-carousel/slide/carousel-slide.component';
import { WINDOW } from '@wizbii/angular-utilities';
import Splide, { SplideOptions } from '@wizbii/splide';

@Component({
  selector: 'app-wzb-carousel',
  template: `
    <div #splideElement class="splide" [ngClass]="containerClass">
      <ng-content select="header"></ng-content>

      <div class="splide__track">
        <ul class="splide__list">
          <li
            class="splide__slide"
            *ngFor="let slide of slides; let i = index; trackBy: trackBySlide"
            (keydown.enter)="onKeyPressed(i)"
          >
            <ng-template *ngIf="slide.slideContent" [ngTemplateOutlet]="slide.slideContent"></ng-template>
          </li>
        </ul>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WzbCarouselComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() selectedSlideIndex!: number;
  @Input() title?: string;
  @Input() externalButton?: boolean;
  @Input() maxSlideIndex!: number;
  @Input() options: SplideOptions = {};
  @Input() containerClass!: string;

  @Output() selectedSlideIndexChange = new EventEmitter<number>();
  @Output() init = new EventEmitter<any>();
  @Output() splideEvent = new EventEmitter<any>();

  @Output() mounted = new EventEmitter();
  @Output() updated = new EventEmitter();
  @Output() move = new EventEmitter();
  @Output() moved = new EventEmitter();
  @Output() isDrag = new EventEmitter();
  @Output() isDragged = new EventEmitter();
  @Output() visible = new EventEmitter();
  @Output() isHidden = new EventEmitter();
  @Output() active = new EventEmitter();
  @Output() inactive = new EventEmitter();
  @Output() isClicked = new EventEmitter();
  @Output() arrowsMounted = new EventEmitter();
  @Output() arrowsUpdated = new EventEmitter();
  @Output() paginationMounted = new EventEmitter();
  @Output() paginationUpdated = new EventEmitter();
  @Output() navigationMounted = new EventEmitter();
  @Output() autoplayPlay = new EventEmitter();
  @Output() autoplayPause = new EventEmitter();
  @Output() autoplayPlaying = new EventEmitter();
  @Output() lazyloadLoaded = new EventEmitter();

  @ContentChildren(WzbCarouselSlideComponent) slides!: QueryList<WzbCarouselSlideComponent>;

  @ViewChild('splideElement') splideElement!: ElementRef;
  protected splide!: any;

  constructor(private readonly cdr: ChangeDetectorRef, @Inject(WINDOW) private readonly window: any) {}

  ngAfterViewInit(): void {
    this.window.setTimeout(() => {
      this.splide = new Splide(this.splideElement.nativeElement, this.options);
      this.init.emit(this.splide);
      this.addEventListeners();
      this.splide.mount();
    });

    this.slides.changes.subscribe(() => {
      this.cdr.detectChanges();

      this.window.setTimeout(() => {
        this.splide.destroy();
        this.splide.mount();

        this.addEventListeners();
      });
    });
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.splide) {
      return;
    }

    if (changes['selectedSlideIndex']) {
      const currentIndex = changes.selectedSlideIndex?.currentValue;

      if (currentIndex !== this.splide.index) {
        this.splide.go(currentIndex);
      }
    }

    if (changes['options']) {
      this.splide.options = changes.options?.currentValue;
    }
  }

  addEventListeners(): void {
    this.splide
      .on(WizbiiCarouselActions.MOUNTED, (...args: any): void => {
        this.mounted.emit(args);
        this.splideEvent.emit({
          name: WizbiiCarouselActions.MOUNTED,
          args,
        });
      })
      .on(WizbiiCarouselActions.UPDATED, (...args: any) => {
        this.updated.emit(args);
        this.splideEvent.emit({
          name: WizbiiCarouselActions.UPDATED,
          args,
        });
      })
      .on(WizbiiCarouselActions.MOVE, (newIndex: number, oldIndex: any, destIndex: any) => {
        this.move.emit([newIndex, oldIndex, destIndex]);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.MOVE,
          args: [newIndex, oldIndex, destIndex],
        });
      })
      .on(WizbiiCarouselActions.MOVED, (...args: number[]): void => {
        const newIndex = args[0];
        this.selectedSlideIndexChange.emit(newIndex);

        this.moved.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.MOVED,
          args,
        });
      })
      .on(WizbiiCarouselActions.DRAG, (...args: any): void => {
        this.isDrag.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.DRAG,
          args,
        });
      })
      .on(WizbiiCarouselActions.DRAGGED, (...args: any): void => {
        this.isDragged.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.DRAGGED,
          args,
        });
      })
      .on(WizbiiCarouselActions.VISIBLE, (...args: any): void => {
        this.visible.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.VISIBLE,
          args,
        });
      })
      .on(WizbiiCarouselActions.HIDDEN, (...args: any): void => {
        this.isHidden.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.HIDDEN,
          args,
        });
      })
      .on(WizbiiCarouselActions.ACTIVE, (...args: any): void => {
        this.active.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.ACTIVE,
          args,
        });
      })
      .on(WizbiiCarouselActions.INACTIVE, (...args: any): void => {
        this.inactive.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.INACTIVE,
          args,
        });
      })
      .on(WizbiiCarouselActions.CLICK, (...args: any): void => {
        this.isClicked.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.CLICK,
          args,
        });
      })
      .on(WizbiiCarouselActions.ARROWS_MOUNTED, (...args: any): void => {
        this.arrowsMounted.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.ARROWS_MOUNTED,
          args,
        });
      })
      .on(WizbiiCarouselActions.ARROWS_UPDATED, (...args: any): void => {
        this.arrowsUpdated.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.ARROWS_UPDATED,
          args,
        });
      })
      .on(WizbiiCarouselActions.PAGINATION_MOUNTED, (...args: any): void => {
        this.paginationMounted.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.PAGINATION_MOUNTED,
          args,
        });
      })
      .on(WizbiiCarouselActions.PAGINATION_UPDATED, (...args: any): void => {
        this.paginationUpdated.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.PAGINATION_UPDATED,
          args,
        });
      })
      .on(WizbiiCarouselActions.NAVIGATION_MOUNTED, (...args: any): void => {
        this.navigationMounted.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.NAVIGATION_MOUNTED,
          args,
        });
      })
      .on(WizbiiCarouselActions.AUTOPLAY_PLAY, (...args: any): void => {
        this.autoplayPlay.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.AUTOPLAY_PLAY,
          args,
        });
      })
      .on(WizbiiCarouselActions.AUTOPLAY_PAUSE, (...args: any): void => {
        this.autoplayPause.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.AUTOPLAY_PAUSE,
          args,
        });
      })
      .on(WizbiiCarouselActions.AUTOPLAY_PLAYING, (...args: any): void => {
        this.autoplayPlaying.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.AUTOPLAY_PLAYING,
          args,
        });
      })
      .on(WizbiiCarouselActions.LAZYLOADING_LOADED, (...args: any): void => {
        this.lazyloadLoaded.emit(args);

        this.splideEvent.emit({
          name: WizbiiCarouselActions.LAZYLOADING_LOADED,
          args,
        });
      });
  }

  trackBySlide(index: number): number {
    return index;
  }

  onKeyPressed(i: number): void {
    const args = [{ index: i }];

    this.isClicked.emit(args);
  }

  ngOnDestroy(): void {
    if (this.splide) {
      this.splide.destroy(true);
    }
  }
}

@NgModule({
  declarations: [WzbCarouselComponent, WzbCarouselSlideComponent],
  imports: [CommonModule],
  exports: [WzbCarouselComponent, WzbCarouselSlideComponent],
})
export class WzbCarouselModule {}
